// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================
// Business logic and API calls for Authentication and Authorization module

import { get, post, put, del, patch } from '../client/axiosClient';
import { authEndpoints, userEndpoints } from '../endpoints';
import { tokenUtils } from '../config/apiConfig';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  LogoutRequest,
  LogoutResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  UserSession,
  PermissionCheckRequest,
  PermissionCheckResponse,
  // Tenant Hub Two-Step Auth Types
  DiscoverRequest,
  DiscoverResponse,
  AuthenticateRequest,
  AuthenticateResponse,
  DiscoveredTenant,
} from '../types';

/**
 * Authentication Service Class
 */
class AuthService {
  // ============================================================================
  // TENANT HUB TWO-STEP AUTHENTICATION (Recommended)
  // ============================================================================

  /**
   * Extract roles from a Keycloak JWT access token.
   * Reads resource_access.{clientId}.roles from the token payload.
   * Used as fallback when the Tenant Hub API does not return roles in the response.
   */
  private extractRolesFromToken(accessToken: string, clientId: string = 'das') {
    try {
      const payloadBase64 = accessToken.split('.')[1];
      if (!payloadBase64) return [];
      const payload = JSON.parse(atob(payloadBase64));
      const resourceRoles: string[] = payload?.resource_access?.[clientId]?.roles || [];
      return resourceRoles.map(role => ({
        id: role,
        code: role,
        name: role,
        roleType: 'keycloak',
        assignmentType: 'direct',
      }));
    } catch {
      return [];
    }
  }

  /**
   * Extract user-friendly error message from axios error
   */
  private extractErrorMessage(error: any, defaultMessage: string): string {
    // Try to get message from response data (various formats)
    const responseData = error?.response?.data;
    if (responseData) {
      // Check common API response formats
      if (typeof responseData.message === 'string' && responseData.message) {
        return responseData.message;
      }
      if (typeof responseData.error === 'string' && responseData.error) {
        return responseData.error;
      }
      if (typeof responseData.title === 'string' && responseData.title) {
        return responseData.title;
      }
      // Handle array of errors
      if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        return responseData.errors[0];
      }
    }

    // Provide user-friendly messages based on HTTP status code
    const status = error?.response?.status;
    if (status) {
      switch (status) {
        case 400:
          return 'Invalid request. Please check your input and try again.';
        case 401:
          return 'Invalid username or password. Please try again.';
        case 403:
          return 'Access denied. You do not have permission to perform this action.';
        case 404:
          return 'Service not available. Please try again later.';
        case 500:
          return 'Server error. Please try again later.';
        case 503:
          return 'Service temporarily unavailable. Please try again later.';
      }
    }

    // Check for network errors
    if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    // Return default message (never show technical axios messages)
    return defaultMessage;
  }

  /**
   * Step 1: Discover accessible tenants and applications for user credentials
   * Returns list of tenants/apps the user can access
   */
  async discover(credentials: DiscoverRequest): Promise<DiscoverResponse> {
    try {
      const response = await post<DiscoverResponse>(authEndpoints.discover(), credentials);
      return response.data;
    } catch (error: any) {
      // Extract user-friendly error message
      const errorMessage = this.extractErrorMessage(error, 'Invalid username or password. Please try again.');

      // Create an error with the user-friendly message
      const authError = new Error(errorMessage);
      (authError as any).response = error?.response;
      throw authError;
    }
  }

  /**
   * Step 2: Authenticate with selected tenant and application
   * Returns JWT token and user permissions
   */
  async authenticate(request: AuthenticateRequest): Promise<AuthenticateResponse> {
    let authData: AuthenticateResponse;

    try {
      const response = await post<AuthenticateResponse>(authEndpoints.authenticate(), request);
      authData = response.data;
    } catch (error: any) {
      // Extract user-friendly error message
      const errorMessage = this.extractErrorMessage(error, 'Authentication failed. Please try again.');

      // Create an error with the user-friendly message
      const authError = new Error(errorMessage);
      (authError as any).response = error?.response;
      throw authError;
    }

    // Store token and user context on successful authentication
    if (authData.success && authData.data) {
      // Use accessToken (backend property name)
      tokenUtils.setToken(authData.data.accessToken);

      // Store refresh token for silent token refresh on 401
      if (authData.data.refreshToken) {
        tokenUtils.setRefreshToken(authData.data.refreshToken);
      }

      // Store user context for API headers (flat properties from backend)
      if (authData.data.userId) {
        tokenUtils.setUserId(authData.data.userId);
      }

      // Store tenant context
      if (authData.data.tenantId) {
        tokenUtils.setTenantId(authData.data.tenantId);
      }
      if (authData.data.tenantName) {
        tokenUtils.setTenantName(authData.data.tenantName);
      }
      if (authData.data.tenantDisplayName) {
        tokenUtils.setTenantDisplayName(authData.data.tenantDisplayName);
      }
      if (authData.data.tenantLogoBase64) {
        tokenUtils.setTenantLogoBase64(authData.data.tenantLogoBase64);
      }
      if (authData.data.applicationId) {
        tokenUtils.setAppId(authData.data.applicationId);
      }

      // Resolve roles. In DAS, the user object must carry ONLY das-client
      // roles (from JWT resource_access.das.roles) — never roles from
      // sibling apps (prima/recsuit/fbk/etc.) in the same shared Keycloak
      // realm. Tenant Hub's permissions.roles reflects the token's `azp`
      // (authorized party) which may not be "das" when the user logged
      // into a sibling app first; surfacing those roles would either
      // (a) wrongly reject DAS access at the role-gate or (b) wrongly
      // grant DAS access for an app role with no DAS meaning.
      // A user may hold multiple DAS roles simultaneously — all of them
      // are preserved here.
      const roles = authData.data.accessToken
        ? this.extractRolesFromToken(authData.data.accessToken)
        : [];

      // Always overwrite permissions.roles with the das-only set so
      // downstream callers (useAuth role-gate, /by-roles lookup) see the
      // correct DAS roles regardless of what Tenant Hub returned.
      authData.data.permissions = {
        ...(authData.data.permissions ?? {}),
        roles,
      };

      // Build user object from flat properties and store for display
      const userData = {
        id: authData.data.userId,
        username: authData.data.userName,
        email: authData.data.userEmail,
        firstName: authData.data.userFirstName,
        lastName: authData.data.userLastName,
        fullName: `${authData.data.userFirstName || ''} ${authData.data.userLastName || ''}`.trim() || authData.data.userName,
        roles,
      };
      tokenUtils.setUserData(userData);
    }

    return authData;
  }

  /**
   * Two-step login: Combines discover and authenticate for simple cases
   * Automatically selects the recommended tenant if only one is available
   */
  async loginWithTenantHub(
    credentials: DiscoverRequest,
    selectTenant?: (tenants: DiscoveredTenant[]) => DiscoveredTenant | null
  ): Promise<AuthenticateResponse> {
    // Step 1: Discover accessible tenants
    const discoverResponse = await this.discover(credentials);

    if (!discoverResponse.success || !discoverResponse.data) {
      throw new Error(discoverResponse.message || 'Discovery failed');
    }

    const { accessibleTenants, recommendedTenant } = discoverResponse.data;

    // Filter to tenants that have the DAS application registered. A user
    // may be assigned to multiple Tenant Hub applications (prima, recsuit,
    // fbk, account, das, ...) — but THIS frontend is the DAS app, so we
    // must NEVER fall through to a sibling app's token. If the
    // recommended tenant lacks DAS but another tenant has it, we use that
    // other tenant.
    const dasTenants = (accessibleTenants ?? []).filter(t =>
      (t.applications ?? []).some(a => a.applicationCode?.toLowerCase() === 'das')
    );

    if (dasTenants.length === 0) {
      throw new Error(
        'No DAS application is available for this account. Please contact the administrator.'
      );
    }

    // Selection priority within DAS-enabled tenants:
    //   1. Caller-supplied selector (if any) — but restricted to DAS tenants
    //   2. Recommended tenant (if it has DAS)
    //   3. First DAS-enabled tenant
    let selectedTenant: DiscoveredTenant | null = null;

    if (selectTenant) {
      const userChoice = selectTenant(dasTenants);
      if (userChoice && dasTenants.includes(userChoice)) {
        selectedTenant = userChoice;
      }
    }
    if (!selectedTenant && recommendedTenant && dasTenants.includes(recommendedTenant)) {
      selectedTenant = recommendedTenant;
    }
    if (!selectedTenant) {
      selectedTenant = dasTenants[0];
    }

    // Find the DAS application strictly (no first-app fallback — that's
    // the bug that previously sent users into the prima app).
    const dasApp = selectedTenant.applications.find(
      app => app.applicationCode?.toLowerCase() === 'das'
    );

    if (!dasApp) {
      // Should be unreachable given the dasTenants filter, but defend
      // anyway so we never authenticate against a non-DAS app.
      throw new Error(
        'The selected tenant does not have access to the DAS application.'
      );
    }

    // Step 2: Authenticate with selected tenant and application
    const authenticateRequest: AuthenticateRequest = {
      username: credentials.username,
      password: credentials.password,
      selectedTenantId: selectedTenant.tenantId,
      selectedApplicationId: dasApp.applicationId,
    };

    return this.authenticate(authenticateRequest);
  }

  // ============================================================================
  // LEGACY AUTHENTICATION (For backward compatibility)
  // ============================================================================

  /**
   * User login (DEPRECATED - use loginWithTenantHub instead)
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await post<LoginResponse>(authEndpoints.login(), credentials);
    const loginData = response.data;

    // Store token (backend returns flat structure, not nested in data)
    if (loginData.success && loginData.token) {
      tokenUtils.setToken(loginData.token);
      // Note: refreshToken not implemented in current backend

      // Store user context for API headers (user ID is used for filtering requests)
      if (loginData.user?.id) {
        tokenUtils.setUserId(loginData.user.id as string);
      }

      // Store full user data for display in the UI (sidebar, header, etc.)
      if (loginData.user) {
        tokenUtils.setUserData(loginData.user);
      }
    }

    return loginData;
  }

  /**
   * User logout
   */
  async logout(request?: LogoutRequest): Promise<LogoutResponse> {
    const refreshToken = request?.refreshToken || tokenUtils.getRefreshToken();
    const response = await post<LogoutResponse>(authEndpoints.logout(), {
      refreshToken,
    });

    // Clear tokens
    tokenUtils.clearTokens();

    return response.data;
  }

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await post<RegisterResponse>(authEndpoints.register(), data);
    return response.data;
  }

  /**
   * Refresh access token via Tenant Hub
   */
  async refreshToken(request?: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const refreshToken = request?.refreshToken || tokenUtils.getRefreshToken();
    const tenantId = request?.tenantId || tokenUtils.getTenantId();
    const applicationId = request?.applicationId || tokenUtils.getAppId();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    if (!tenantId) {
      throw new Error('No tenant ID available for token refresh');
    }

    if (!applicationId) {
      throw new Error('No application ID available for token refresh');
    }

    // Call the Tenant Hub refresh endpoint with dynamic client credentials
    const response = await post<AuthenticateResponse>(authEndpoints.refreshHub(), {
      refreshToken,
      tenantId,
      applicationId,
    });

    const authData = response.data;

    // Store new tokens (Tenant Hub response format)
    if (authData.success && authData.data) {
      tokenUtils.setToken(authData.data.accessToken);
      if (authData.data.refreshToken) {
        tokenUtils.setRefreshToken(authData.data.refreshToken);
      }
    }

    // Convert to RefreshTokenResponse format for backward compatibility
    return {
      success: authData.success,
      data: {
        token: authData.data?.accessToken || '',
        refreshToken: authData.data?.refreshToken || '',
        expiresIn: authData.data?.expiresIn || 0,
      },
    };
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await get<ApiResponse<User>>(authEndpoints.me());
    const userData = response.data;

    // Store user context for API headers (in case of page refresh)
    if (userData.success && userData.data?.id) {
      tokenUtils.setUserId(userData.data.id as string);
    }

    return userData;
  }

  /**
   * Update current user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<User>> {
    const response = await put<ApiResponse<User>>(authEndpoints.updateProfile(), data);
    return response.data;
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<EmptyResponse> {
    const tenantId = tokenUtils.getTenantId();
    const userId = tokenUtils.getUserId();

    if (!tenantId || !userId) {
      throw new Error('Tenant ID and User ID are required for password change');
    }

    const response = await put<EmptyResponse>(authEndpoints.changePassword(tenantId, userId), data);
    return response.data;
  }

  /**
   * Forgot password
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.forgotPassword(), data);
    return response.data;
  }

  /**
   * Reset password
   */
  async resetPassword(data: ResetPasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.resetPassword(), data);
    return response.data;
  }

  /**
   * Verify email
   */
  async verifyEmail(token: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.verifyEmail(), { token });
    return response.data;
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.resendVerification(), {
      email,
    });
    return response.data;
  }

  /**
   * Check permission
   */
  async checkPermission(
    request: PermissionCheckRequest
  ): Promise<ApiResponse<PermissionCheckResponse>> {
    const response = await post<ApiResponse<PermissionCheckResponse>>(
      authEndpoints.checkPermission(),
      request
    );
    return response.data;
  }

  /**
   * Get user sessions
   */
  async getSessions(): Promise<ListResponse<UserSession>> {
    const response = await get<ListResponse<UserSession>>(authEndpoints.sessions());
    return response.data;
  }

  /**
   * Revoke session
   */
  async revokeSession(sessionId: string): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(authEndpoints.revokeSession(sessionId));
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return tokenUtils.isAuthenticated();
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return tokenUtils.getToken();
  }

  // ============================================================================
  // USER MANAGEMENT METHODS (Admin)
  // ============================================================================

  /**
   * Get all users
   */
  async getUsers(params?: any): Promise<ListResponse<User>> {
    const endpoint = params
      ? buildEndpointWithQuery(userEndpoints.list(), params)
      : userEndpoints.list();

    const response = await get<ListResponse<User>>(endpoint);
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getUserById(id: ID): Promise<ApiResponse<User>> {
    const response = await get<ApiResponse<User>>(userEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new user
   */
  async createUser(data: RegisterRequest): Promise<ApiResponse<User>> {
    const response = await post<ApiResponse<User>>(userEndpoints.create(), data);
    return response.data;
  }

  /**
   * Update user
   */
  async updateUser(id: ID, data: Partial<User>): Promise<ApiResponse<User>> {
    const response = await put<ApiResponse<User>>(userEndpoints.update(id), data);
    return response.data;
  }

  /**
   * Delete user
   */
  async deleteUser(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(userEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search users
   */
  async searchUsers(params: any): Promise<ListResponse<User>> {
    const response = await post<ListResponse<User>>(userEndpoints.search(), params);
    return response.data;
  }

  /**
   * Get active users
   */
  async getActiveUsers(): Promise<ListResponse<User>> {
    const response = await get<ListResponse<User>>(userEndpoints.active());
    return response.data;
  }

  /**
   * Update user status
   */
  async updateUserStatus(id: ID, isActive: boolean): Promise<EmptyResponse> {
    const response = await patch<EmptyResponse>(userEndpoints.updateStatus(id), {
      isActive,
    });
    return response.data;
  }

  /**
   * Assign role to user
   */
  async assignRole(id: ID, role: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(userEndpoints.assignRole(id), { role });
    return response.data;
  }

  /**
   * Assign permissions to user
   */
  async assignPermissions(id: ID, permissions: string[]): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(userEndpoints.assignPermissions(id), {
      permissions,
    });
    return response.data;
  }

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(userEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const authService = new AuthService();
export default authService;
